import {
  Box,
  Flex,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { Fragment, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { AdminPageLayout } from '../../../components/AdminPageLayout/AdminPageLayout'
import { AdminBadge } from '../../../components/StatusBadge/StatusBadge'
import { useAppStore } from '../../../store/appStore/appStore'
import {
  ProbePassDetailsRow,
  ProbeStatusBadge,
} from '../shared/TableProbeUi.parts'
import { AppStoreTableProbeQueryContext } from '../shared/appStoreTableProbeQueryContext.parts'
import { PROBE_CONCURRENCY } from '../shared/probeConcurrency.parts'
import { TableProbeOrchestrator } from '../shared/tableProbeOrchestrator.parts'
import {
  ListTableProbeRow,
  ListTablesTest,
  ProbeStatus,
} from './ListTablesTestPage.parts'

const LIST_TABLE_COLUMN_COUNT = 7

export const ListTablesTestPage = () => {
  const viewConfigs = useAppStore(store => store.cms.viewConfigs)
  const [rows, setRows] = useState<ListTableProbeRow[]>([])
  const [expandedQueryId, setExpandedQueryId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const targets = ListTablesTest.collectMainListTables(viewConfigs)

    setRows(ListTablesTest.toInitialProbeRows(targets))
    setExpandedQueryId(null)

    const runProbes = async () => {
      const context = AppStoreTableProbeQueryContext.create()

      await TableProbeOrchestrator.runListTableProbes(context, viewConfigs, {
        onRowProgress: (queryId, recordsChecked, totalCount) => {
          if (cancelled) {
            return
          }

          setRows(current =>
            current.map(row =>
              row.queryId === queryId
                ? {
                    ...row,
                    recordsChecked,
                    totalCount,
                  }
                : row,
            ),
          )
        },
        onRowUpdate: (queryId, probeResult) => {
          if (cancelled) {
            return
          }

          setRows(current =>
            current.map(row =>
              row.queryId === queryId
                ? {
                    ...row,
                    ...probeResult,
                  }
                : row,
            ),
          )
        },
      })
    }

    void runProbes()

    return () => {
      cancelled = true
      targets.forEach(target => {
        useAppStore
          .getState()
          .queryStore.removeQuery(
            ListTablesTest.createProbeQueryId(target.queryId),
          )
      })
    }
  }, [viewConfigs])

  const isRunning = rows.some(row => row.status === 'running')
  const passCount = rows.filter(row => row.status === 'pass').length

  const toggleExpanded = (queryId: string, status: ProbeStatus) => {
    if (status === 'running') {
      return
    }

    setExpandedQueryId(current => (current === queryId ? null : queryId))
  }

  return (
    <AdminPageLayout pageTitle="List Tables Test" icon="CheckIcon">
      <Flex alignItems="center" gap="12px" mb="16px">
        <AdminBadge
          colorScheme={
            isRunning ? 'blue' : passCount === rows.length ? 'green' : 'orange'
          }
        >
          {isRunning
            ? 'Running probes...'
            : `${passCount}/${rows.length} lists returned data`}
        </AdminBadge>
        {isRunning && <Spinner size="sm" />}
      </Flex>

      <Text fontSize="sm" color="presets.neutral.60" mb="12px">
        Lists probe concurrently (up to {PROBE_CONCURRENCY} at a time). Each
        list stops at the first row with data. Click a completed row to copy the
        passing record link.
      </Text>

      <Box
        border="1px solid"
        borderColor="presets.neutral.20"
        borderRadius="8px"
        overflow="hidden"
      >
        <Table size="sm" variant="simple">
          <Thead bg="presets.neutral.10">
            <Tr>
              <Th>List</Th>
              <Th>Route</Th>
              <Th>Query ID</Th>
              <Th>Status</Th>
              <Th isNumeric>Total Count</Th>
              <Th isNumeric>Records Checked</Th>
              <Th>Sample Value</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map(row => (
              <Fragment key={row.queryId}>
                <Tr
                  data-testid={`list-table-test-row-${row.queryId}`}
                  cursor={row.status === 'running' ? 'default' : 'pointer'}
                  onClick={() => toggleExpanded(row.queryId, row.status)}
                  _hover={
                    row.status === 'running'
                      ? undefined
                      : { bg: 'presets.neutral.5' }
                  }
                >
                  <Td fontWeight="600">{row.viewLabel}</Td>
                  <Td>
                    <Link
                      as={RouterLink}
                      to={row.passRecordPath ?? row.viewPath}
                      color="presets.turquoise.100"
                      fontSize="sm"
                      onClick={event => event.stopPropagation()}
                    >
                      {row.viewPath}
                    </Link>
                  </Td>
                  <Td>
                    <Box as="code" fontSize="xs">
                      {row.queryId}
                    </Box>
                  </Td>
                  <Td>
                    <ProbeStatusBadge
                      status={row.status}
                      data-testid={`list-table-test-status-${row.queryId}`}
                    />
                    {row.status === 'running' &&
                      row.recordsChecked !== undefined && (
                        <Text mt="4px" fontSize="xs" color="presets.neutral.60">
                          Checked {row.recordsChecked}
                          {row.totalCount !== undefined
                            ? ` / ${row.totalCount}`
                            : ''}
                        </Text>
                      )}
                  </Td>
                  <Td isNumeric>
                    {row.totalCount ?? (row.status === 'running' ? '—' : 0)}
                  </Td>
                  <Td isNumeric>
                    {row.recordsChecked ?? (row.status === 'running' ? '—' : 0)}
                  </Td>
                  <Td>
                    {row.sampleValue ?? (row.status === 'running' ? '—' : '—')}
                  </Td>
                </Tr>
                <ProbePassDetailsRow
                  columnCount={LIST_TABLE_COLUMN_COUNT}
                  isExpanded={expandedQueryId === row.queryId}
                  passRecordPath={row.passRecordPath}
                  recordsChecked={row.recordsChecked}
                  status={row.status}
                  errorMessage={row.errorMessage}
                />
              </Fragment>
            ))}
          </Tbody>
        </Table>
      </Box>
    </AdminPageLayout>
  )
}
